import { ActivatedRoute } from '@angular/router';
import { Configuration2D } from 'src/app/models/configurators-2d/configuration2d';
import { BaseAppService } from 'src/app/services/configurators-2d/base-app-service';
import { ConfiguratorStructure } from './configurator-structure';
import { ConfiguratorStructureStep } from './configurator-structure-step';
import { ConfiguratorItem } from './configurator-item';
import { InjectionToken, Injector } from '@angular/core';
import { IBaseComponent } from './i-base-component';
import { ISummaryComponent } from './i-summary-component';

export abstract class BaseConfigurator<C extends Configuration2D, S extends BaseAppService<C>> extends ConfiguratorItem<C, S> {
  private configuratorStructure: ConfiguratorStructure = undefined;

  private readonly components = new Map<InjectionToken<IBaseComponent>, IBaseComponent>();
  private previousProjectId: string = undefined;

  constructor(service: S, protected injector: Injector) {
    super(service);
  }

  /**
   * Fonction d'initialisation de la structure du configurateur
   */
  abstract initConfigurator(): ConfiguratorStructure;

  /**
   * Fonction d'activation du bouton Enregistrer
   */
  isSaveProjectButtonDisabled(): boolean {
    return !this.configuration.location;
  }

  /**
   * Fonction d'activation du bouton suivant
   * @returns booléen d'activation du bouton suivant
   */
  isNextButtonActive(step: number): boolean {
    let component = this.getConfiguratorStepComponent(step);
    if (component !== undefined) {
      return component.isNextButtonActive();
    }

    return false;
  }

  isNextButtonHidden(step: number, finalStep: number): boolean {
    if (step < finalStep) {
      return false;
    }

    return true;
  }

  /**
   * Fonction d'activation du bouton order
   * @returns booléen d'activation du bouton order
   */
  isOrderButtonDisabled(step: number): boolean {
    let component = this.getConfiguratorStepComponent(step);

    if (component !== undefined && this.isISummaryComponent(component)) {
      return component.isOrderButtonDisabled();
    }
    return false;
  }

  private isISummaryComponent(component: any): component is ISummaryComponent {
    return !!component.isOrderButtonDisabled;
  }

  /**
   * Fonction de check si la page est accessible ou non
   * @param step étape de la configuration
   * @returns boolean
   */
  checkPageChangeCapability(step: number): boolean {
    let component = this.getConfiguratorStepComponent(step);
    if (component !== undefined) {
      return component.checkPageChangeCapability();
    }

    return false;
  }

  get configurator() {
    this.configuratorStructure ??= this.initConfigurator();
    return this.configuratorStructure;
  }

  /**
   * Fonction d'initialisation du stepper
   * @returns tableau des steps
   */
  get steps(): ConfiguratorStructureStep[] {
    return this.configurator.steps;
  }

  /**
   * Fonction de récupération des infos de l'étape du configurateur souhaitée
   * @param step étape du configurateur à récupérer
   * @returns données de l'étape du configurateur souhaitée
   */
  protected getConfiguratorStepComponent(step: number): IBaseComponent {
    let stepStructure = this.steps.find(cs => cs.index === step);
    if (stepStructure !== undefined) {
      if (this.components.has(stepStructure.injectionToken)) {
        return this.components.get(stepStructure.injectionToken);
      }

      let component = this.injector.get(stepStructure.injectionToken);
      this.components.set(stepStructure.injectionToken, component);
      return component;
    }

    return undefined;
  }

  /**
   * Fonction pour récupérer la configuration existante si son ID est présent dans l'URL
   * @param route ActivatedRoute du component appelant
   * @param callback
   * @returns true si une configuration a été récupérée avec succès, false sinon
   */
  protected loadExistingConfigurationIfNeeded(
    route: ActivatedRoute,
    callback?: (projectIdChange: boolean) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      route.params.subscribe(params => {
        //Si un ID de configuration est présent dans l'URL, on récupère cette configuration
        if (params.projectId !== undefined) {
          //Récupération du projet par l'id
          this.service.getProjectById(route.snapshot.params.projectId, true).then(r => {
            if (callback && this.previousProjectId != undefined) {
              callback(true);
            }
            this.previousProjectId = params.projectId;
            resolve();
          });
        } else {
          if (callback) {
            callback(false);
          }

          this.previousProjectId = undefined;
          resolve();
        }
      });
    });
  }

  /**
   * Fonction pour récupérer le background image de l'emplacement
   * @returns URL de l'image de fond pour la visualisation
   */
  getEmplacementPictureUrl(): string {
    return this.configuration.location?.picture?.path;
  }

  /**
   * Fonction pour vérifier s'il y a un service de pose
   */
  async checkInstallation(): Promise<boolean> {
    return true;
  }

  /**
   * Fonction de vérification de la commande
   */
  async checkOrder(): Promise<boolean> {
    return true;
  }
}
